import { configureStore } from "@reduxjs/toolkit";
import {
  TypedUseSelectorHook,
  useDispatch as useAppDispatch,
  useSelector as useAppSelector,
} from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { STORE_KEY } from "@/configs";

import reducers from "./reducers";

// The session token lives in memory only; a reload gets it back from the httpOnly cookie
// (lib/session.ts). Nothing else is persisted today.
const persistConfig = {
  key: STORE_KEY,
  storage,
  whitelist: [] as string[],
};

// Builds before this one persisted `auth`, token included: taken out of storage before
// redux-persist rehydrates it, and handed once to the session restore.
let legacySessionToken: string | null = null;
try {
  const persistKey = `persist:${STORE_KEY}`;
  const raw = localStorage.getItem(persistKey);
  const persisted = raw ? (JSON.parse(raw) as Record<string, string>) : null;
  if (persisted?.auth) {
    legacySessionToken = (JSON.parse(persisted.auth) as { token?: string | null }).token || null;
    delete persisted.auth;
    localStorage.setItem(persistKey, JSON.stringify(persisted));
  }
} catch {
  // Unreadable storage: nothing to carry over.
}

export const takeLegacySessionToken = (): string | null => {
  const token = legacySessionToken;
  legacySessionToken = null;
  return token;
};

const persistedReducer = persistReducer(persistConfig, reducers);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

const persister = persistStore(store);

export type RootState = ReturnType<typeof reducers>;

export type AppDispatch = typeof store.dispatch;

const { dispatch } = store;

const useDispatch = () => useAppDispatch<AppDispatch>();
const useSelector: TypedUseSelectorHook<RootState> = useAppSelector;

export { dispatch, persister, store, useDispatch, useSelector };

export * from "./reducers/auth";
